// One spelling for a day, for the whole portal (module 13 DESIGN, "One day, one spelling"). A month's short name is written from this
// table and never asked of `Intl`: `month: 'short'` in en-GB is whatever the ICU of the browser, the phone or the server happens to
// carry ("Sept" since CLDR 38, "Sep" before it), so two machines, or a card and the server-written line under it, spell one day two
// ways. The table is the spelling the approved screens draw and the one the server's own writers use (the ask lane's `shortDate`, the
// ranker's words). Pure, tested.

export const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/** Whose calendar the day is read on: this computer's, or UTC for a date that carries no time of day (a period, a contract date). */
export type DayZone = 'local' | 'utc';

/** "Sep", for a format that builds round the month ("Sep 26", "Thu 18 Sep"). */
export function monthShort(d: Date, zone: DayZone = 'local'): string {
  return MONTHS_SHORT[zone === 'utc' ? d.getUTCMonth() : d.getMonth()] ?? '';
}

/** "11 Sep". */
export function dayMonth(d: Date, zone: DayZone = 'local'): string {
  return `${zone === 'utc' ? d.getUTCDate() : d.getDate()} ${monthShort(d, zone)}`;
}

/** "11 Sep 2026". */
export function dayMonthYear(d: Date, zone: DayZone = 'local'): string {
  return `${dayMonth(d, zone)} ${zone === 'utc' ? d.getUTCFullYear() : d.getFullYear()}`;
}

// A day with its minute is always this computer's clock: an instant has a time of day, so it is never one of the UTC dates above.
const two = (n: number): string => String(n).padStart(2, '0');
const hourMinute = (d: Date): string => `${two(d.getHours())}:${two(d.getMinutes())}`;

/** "11 Sep, 07:05", punctuated as en-GB punctuates it. */
export function dayMonthTime(d: Date): string {
  return `${dayMonth(d)}, ${hourMinute(d)}`;
}

/** "11 Sep 2026, 07:05". */
export function dayMonthYearTime(d: Date): string {
  return `${dayMonthYear(d)}, ${hourMinute(d)}`;
}
